import type { Control, ControlStatus } from '@wedpo/soc2';
import { OwnerBadge } from '@/components/common';

type Assessed = Control & { status: ControlStatus };

const STATUS_LABEL: Record<string, { c: string; l: string }> = {
  met: { c: 'ok', l: 'Ready' },
  partial: { c: 'warn', l: 'Partial' },
  gap: { c: 'bad', l: 'Gap' },
  na: { c: 'idle', l: 'N/A' },
};

export function Evidence({
  inScope,
  evidence,
  attachEvidence,
  removeEvidence,
}: {
  inScope: Assessed[];
  evidence: Record<string, string[]>;
  attachEvidence: (code: string, names: string[]) => void;
  removeEvidence: (code: string, name: string) => void;
}) {
  // Everything except N/A should carry evidence.
  const needing = inScope.filter((c) => c.status !== 'na');
  const withEv = needing.filter(
    (c) => (evidence[c.code] ?? []).length > 0,
  ).length;
  const coverage = needing.length
    ? Math.round((withEv / needing.length) * 100)
    : 0;

  return (
    <section>
      <p className="eyebrow">Phase 04 · Evidence</p>
      <h1 className="title">Collect &amp; feedback</h1>
      <p className="sub">
        Upload dated proof that each control is operating. Coverage climbs as you
        attach files; your reviewer's notes will land here once review starts.
      </p>

      <div className="grid4" style={{ marginBottom: '1.5rem' }}>
        <div className="kpi">
          <div className="n">{needing.length}</div>
          <div className="l">controls in scope</div>
        </div>
        <div className="kpi">
          <div className="n">{withEv}</div>
          <div className="l">with evidence</div>
        </div>
        <div className="kpi">
          <div className="n">{Math.max(0, needing.length - withEv)}</div>
          <div className="l">still needed</div>
        </div>
        <div className="kpi">
          <div className="n">{coverage}%</div>
          <div className="l">coverage</div>
        </div>
      </div>

      <div className="panel accent">
        <div className="ph">
          <h3>Evidence by control</h3>
          <span className="hint">
            {withEv}/{needing.length} covered
          </span>
        </div>
        {needing.map((c) => {
          const files = evidence[c.code] ?? [];
          const m = STATUS_LABEL[c.status] ?? STATUS_LABEL.na;
          return (
            <div className="ev-row" key={c.code}>
              <div className="ev-main">
                <div className="ev-name">
                  <span className="ev-code">{c.code}</span> {c.name}
                </div>
                <div className="ev-meta">
                  <OwnerBadge role={c.stakeholder} />
                  <span className={`pill ${m.c}`}>{m.l}</span>
                </div>
              </div>
              <div className="ev-files">
                {files.map((name) => (
                  <span className="gp-echip" key={name}>
                    <span className="gp-eclip">DOC</span>
                    <span className="gp-ename">{name}</span>
                    <button
                      type="button"
                      className="gp-ex"
                      onClick={() => removeEvidence(c.code, name)}
                      aria-label={`Remove ${name}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <label className="gp-ebtn">
                  {files.length ? '+ Add more files' : '+ Upload files'}
                  <input
                    type="file"
                    hidden
                    multiple
                    onChange={(e) => {
                      const names = Array.from(e.target.files ?? []).map(
                        (f) => f.name,
                      );
                      if (names.length) attachEvidence(c.code, names);
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
